import { IconExclamationCircle, IconMapPin } from "@tabler/icons-react";
import { OrganizationProfileModel } from "api";
import Select from "components/shared/select";
import { ending } from "helpers/i18n";
import { paths } from "helpers/paths";
import { formatNumber } from "helpers/system";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
    CargorunAverageRatesByDestinationResponse,
    CargorunAverageRatesByDestinationWeek,
    CargorunIndexesResponse,
    CargorunIndexPeriodType,
    CargorunIndexTrailerFamilyType,
    CargorunIndexWeek,
    getAverageRatesByDestination,
    getCargorunIndexes,
} from "sources/cargorunIndexes";
import { getRegionsByName } from "sources/map";
import ChartPointArrow from "./chart-point-arrow.svg";

type LoadingState = "loading" | "success" | "empty" | "error";
type DisplayMode = "index" | "rate";
type IndexTab = "index" | "directions";
type DirectionState = "unselected" | "loading" | "success" | "insufficient" | "error";

type TrailerOption = {
    label: string;
    value: CargorunIndexTrailerFamilyType | "All";
};

type TrailerFamilyFilter = CargorunIndexTrailerFamilyType | "All";

type ChartTooltipProps = {
    active?: boolean;
    payload?: Array<{ payload?: CargorunIndexWeek }>;
    currency?: OrganizationProfileModel["currency"];
    vatLabel: string;
};

type DirectionTooltipProps = {
    active?: boolean;
    payload?: Array<{ payload?: CargorunAverageRatesByDestinationWeek }>;
    currency?: OrganizationProfileModel["currency"];
    vatLabel: string;
};

type VatModeToggleProps = {
    isVatTop: boolean;
    onChange: (isVatTop: boolean) => void;
};

type DirectionPoint = {
    id: number;
    name: string;
};

type DirectionChartWeek = CargorunAverageRatesByDestinationWeek & {
    percentileRange: number;
};

type CargorunIndexProps = {
    organization?: OrganizationProfileModel;
    history: RouteComponentProps["history"];
    back?: () => void;
};

const trailerOptions: TrailerOption[] = [
    { label: "Все типы", value: "All" },
    { label: "Тент", value: "Tent" },
    { label: "Рефрижератор", value: "Refrigerator" },
];

const directionTrailerOptions: Array<{ label: string; value: CargorunIndexTrailerFamilyType }> = [
    { label: "Тент", value: "Tent" },
    { label: "Рефрижератор", value: "Refrigerator" },
];

const getCityRegionsByName = (params: Parameters<typeof getRegionsByName>[0]) =>
    getRegionsByName(params, "City");

const periods: Array<{ label: string; value: CargorunIndexPeriodType }> = [
    { label: "3 мес", value: "LastThreeMonths" },
    { label: "6 мес", value: "LastSixMonths" },
    { label: "С нач. года", value: "FromStartOfTheYear" },
    { label: "1 год", value: "LastYear" },
    { label: "3 года", value: "LastThreeYears" },
    { label: "За все время", value: "AllTime" },
];

const differences = [
    { key: "weekDifferences", label: "За неделю", period: "последние 7 дней" },
    { key: "monthDifferences", label: "За месяц", period: "последние 30 дней" },
    { key: "yearDifferences", label: "За год", period: "последние 12 месяцев" },
] as const;

const indexBaseValue = 100;
const indexChartMinimum = 90;
const indexMethodologyUrl = "https://cargorun.omnidesk.ru/knowledge_base/item/383966?sid=80801";
const directionRangePaddingRatio = 0.1;
const directionRangeMinimumWidth = 6;
const defaultDirectionNames = {
    from: "Москва",
    to: "Санкт-Петербург",
};

function formatChartWeek(value: string) {
    return moment(value, "YYYY-MM-DD").locale("ru").format("D MMM");
}

function formatWeekRange(value: string) {
    const start = moment(value, "YYYY-MM-DD").locale("ru");
    const end = start.clone().add(6, "days");
    return `${start.format("D MMM")} - ${end.format("D MMM YYYY")}`;
}

function isCompletedWeek(week: { week: string }) {
    const currentWeekStart = moment().startOf("isoWeek");
    return moment(week.week, "YYYY-MM-DD").isBefore(currentWeekStart, "day");
}

function formatDifference(value: number | null | undefined) {
    if (value === null || value === undefined) {
        return "-";
    }

    const sign = value > 0 ? "+" : value < 0 ? "-" : "";
    return `${sign}${formatNumber(Math.abs(value), { maximumFractionDigits: 2 })}%`;
}

function formatRate(value: number) {
    return formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function formatRateUnit(currency?: OrganizationProfileModel["currency"]) {
    const currencyPart = new Intl.NumberFormat("ru-RU", { style: "currency", currency: currency || "RUB" })
        .formatToParts(0)
        .find((part) => part.type === "currency");

    return `${currencyPart?.value || currency || "₽"}/км`;
}

function formatRatePerKilometer(value: number, currency?: OrganizationProfileModel["currency"]) {
    return `${formatRate(value)} ${formatRateUnit(currency)}`;
}

/** Returns the user-facing label for the selected VAT mode. */
function formatVatLabel(isVatTop: boolean) {
    return isVatTop ? "с НДС" : "без НДС";
}

function formatRateDifference(
    currentRate: number,
    difference: number | null | undefined,
    currency?: OrganizationProfileModel["currency"],
) {
    if (difference === null || difference === undefined) {
        return "-";
    }

    const ratio = 1 + difference / 100;
    if (!Number.isFinite(ratio) || ratio <= 0) {
        return "-";
    }

    const absoluteDifference = currentRate - currentRate / ratio;
    const sign = absoluteDifference > 0 ? "+" : absoluteDifference < 0 ? "-" : "";
    return `${sign}${formatRate(Math.abs(absoluteDifference))} ${formatRateUnit(currency)}`;
}

function getDefaultDirectionPoint(points: DirectionPoint[], name: string): DirectionPoint | null {
    return points.find((point) => point.name === name) || null;
}

function rangePosition(value: number, min: number, max: number) {
    if (max <= min) {
        return 50;
    }

    return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

function paddedRange(min: number, max: number) {
    const rangeMin = Math.min(min, max);
    const rangeMax = Math.max(min, max);
    const range = rangeMax - rangeMin;
    const padding = range > 0 ? range * directionRangePaddingRatio : Math.max(1, rangeMax * directionRangePaddingRatio);

    return {
        min: Math.max(0, rangeMin - padding),
        max: rangeMax + padding,
    };
}

function rangeSegmentPosition(start: number, end: number, min: number, max: number) {
    const left = rangePosition(Math.min(start, end), min, max);
    const right = rangePosition(Math.max(start, end), min, max);
    const width = right - left;

    if (width >= directionRangeMinimumWidth) {
        return { left, width };
    }

    const center = left + width / 2;
    return {
        left: Math.min(100 - directionRangeMinimumWidth, Math.max(0, center - directionRangeMinimumWidth / 2)),
        width: directionRangeMinimumWidth,
    };
}

function differenceState(value: number | null | undefined) {
    if (value === null || value === undefined || value === 0) {
        return "text-muted";
    }
    return value > 0 ? "text-success" : "text-danger";
}

function currentIndexArrowClass(value: number | null | undefined) {
    const baseClass = "cargorun-index__current-arrow mr-2";
    if (value === null || value === undefined || value === 0) {
        return `${baseClass} cargorun-index__current-arrow--neutral`;
    }
    return value > 0
        ? `${baseClass} cargorun-index__current-arrow--positive`
        : `${baseClass} cargorun-index__current-arrow--negative`;
}

function differenceArrowClass(value: number | null | undefined) {
    const baseClass = "cargorun-index__difference-arrow";
    if (value === null || value === undefined || value === 0) {
        return `${baseClass} cargorun-index__difference-arrow--neutral`;
    }
    return value > 0
        ? `${baseClass} cargorun-index__difference-arrow--positive`
        : `${baseClass} cargorun-index__difference-arrow--negative`;
}

function differenceArrowContainerClass(value: number | null | undefined) {
    const baseClass = "cargorun-index__difference-icon";
    if (value === null || value === undefined || value === 0) {
        return `${baseClass} cargorun-index__difference-icon--neutral`;
    }
    return value > 0
        ? `${baseClass} cargorun-index__difference-icon--positive`
        : `${baseClass} cargorun-index__difference-icon--negative`;
}

/** Switches all index data between rates with and without VAT. */
function VatModeToggle(props: VatModeToggleProps) {
    const { isVatTop, onChange } = props;

    return (
        <div className="cargorun-index__display-mode" role="group" aria-label="Режим учета НДС">
            <button
                className={
                    isVatTop
                        ? "cargorun-index__display-mode-button cargorun-index__display-mode-button--active"
                        : "cargorun-index__display-mode-button"
                }
                type="button"
                aria-pressed={isVatTop}
                onClick={() => onChange(true)}
            >
                С НДС
            </button>
            <button
                className={
                    !isVatTop
                        ? "cargorun-index__display-mode-button cargorun-index__display-mode-button--active"
                        : "cargorun-index__display-mode-button"
                }
                type="button"
                aria-pressed={!isVatTop}
                onClick={() => onChange(false)}
            >
                Без НДС
            </button>
        </div>
    );
}

function IndexTooltip(props: ChartTooltipProps) {
    const { active, currency, payload, vatLabel } = props;
    const week = payload && payload.length > 0 ? payload[0].payload : undefined;

    if (!active || !week) {
        return null;
    }

    return (
        <div className="cargorun-index__tooltip card shadow-sm">
            <div className="cargorun-index__tooltip-period">{formatWeekRange(week.week)}</div>
            <div className="cargorun-index__tooltip-value border-top pt-2">
                <span>Индекс</span>
                <strong>{formatNumber(week.index, { maximumFractionDigits: 1 })} п.</strong>
            </div>
            <div className="cargorun-index__tooltip-value mt-2">
                <span>Медианная ставка</span>
                <strong>
                    {formatRatePerKilometer(week.median, currency)} {vatLabel}
                </strong>
            </div>
            <div className="cargorun-index__tooltip-value mt-2">
                <span>Средняя ставка</span>
                <strong>
                    {formatRatePerKilometer(week.mean, currency)} {vatLabel}
                </strong>
            </div>
            <div className="cargorun-index__tooltip-value border-top mt-2 pt-2">
                <span>Заявок в выборке</span>
                <strong>{formatNumber(week.count)}</strong>
            </div>
        </div>
    );
}

function DirectionTooltip(props: DirectionTooltipProps) {
    const { active, currency, payload, vatLabel } = props;
    const week = payload && payload.length > 0 ? payload[0].payload : undefined;

    if (!active || !week) {
        return null;
    }

    return (
        <div className="cargorun-index__tooltip card shadow-sm">
            <div className="cargorun-index__tooltip-period">
                {formatWeekRange(week.week)}{" "}
                <span className="text-muted ml-2">
                    {formatNumber(week.count)} {ending(week.count, "заявка", "заявки", "заявок")}
                </span>
            </div>
            <div className="cargorun-index__tooltip-value border-top pt-2">
                <span>Медианная ставка</span>
                <strong>
                    {formatRatePerKilometer(week.median, currency)} {vatLabel}
                </strong>
            </div>
            <div className="cargorun-index__tooltip-value mt-2">
                <span>Средняя ставка</span>
                <strong>
                    {formatRatePerKilometer(week.mean, currency)} {vatLabel}
                </strong>
            </div>
            <div className="cargorun-index__tooltip-value mt-2">
                <span>Р25–Р75</span>
                <strong>
                    {formatRate(week.percentile25)}–{formatRate(week.percentile75)} {formatRateUnit(currency)}{" "}
                    {vatLabel}
                </strong>
            </div>
        </div>
    );
}

function CargorunIndex(props: CargorunIndexProps) {
    const { organization } = props;
    const [periodType, setPeriodType] = useState<CargorunIndexPeriodType>("LastThreeMonths");
    const [trailerFamilyType, setTrailerFamilyType] = useState<TrailerFamilyFilter>("All");
    const [displayMode, setDisplayMode] = useState<DisplayMode>("index");
    const [activeTab, setActiveTab] = useState<IndexTab>("index");
    const [isVatTop, setIsVatTop] = useState(true);
    const [data, setData] = useState<CargorunIndexesResponse | null>(null);
    const [state, setState] = useState<LoadingState>("loading");
    const requestVersion = useRef(0);
    const [from, setFrom] = useState<DirectionPoint | null>(null);
    const [to, setTo] = useState<DirectionPoint | null>(null);
    const [directionTrailerFamilyType, setDirectionTrailerFamilyType] = useState<CargorunIndexTrailerFamilyType | null>(
        "Tent",
    );
    const [directionData, setDirectionData] = useState<CargorunAverageRatesByDestinationResponse | null>(null);
    const [directionState, setDirectionState] = useState<DirectionState>("unselected");
    const directionRequestVersion = useRef(0);
    const defaultDirectionRequestVersion = useRef(0);

    const loadIndexes = useCallback(async () => {
        const version = requestVersion.current + 1;
        requestVersion.current = version;
        setData(null);
        setState("loading");

        try {
            const response = await getCargorunIndexes({
                periodType,
                trailerFamilyType: trailerFamilyType === "All" ? null : trailerFamilyType,
                isVatTop,
            });
            if (requestVersion.current !== version) {
                return;
            }

            if (!response || !Array.isArray(response.weeks) || !response.lastSevenDaysIndex) {
                setState("error");
                return;
            }

            const nextData = { ...response, weeks: response.weeks };
            setData(nextData);
            setState(nextData.weeks.some(isCompletedWeek) ? "success" : "empty");
        } catch (_error) {
            if (requestVersion.current === version) {
                setState("error");
            }
        }
    }, [isVatTop, periodType, trailerFamilyType]);

    useEffect(() => {
        if (activeTab !== "index") {
            return;
        }

        loadIndexes();

        return () => {
            requestVersion.current += 1;
        };
    }, [activeTab, loadIndexes]);

    useEffect(() => {
        let isCancelled = false;
        const version = defaultDirectionRequestVersion.current + 1;
        defaultDirectionRequestVersion.current = version;

        const loadDefaultDirection = async () => {
            const [fromResponse, toResponse] = await Promise.all([
                getCityRegionsByName({
                    filters: [{ dataIndex: "name", value: defaultDirectionNames.from }],
                }),
                getCityRegionsByName({
                    filters: [{ dataIndex: "name", value: defaultDirectionNames.to }],
                }),
            ]);

            if (isCancelled || defaultDirectionRequestVersion.current !== version) {
                return;
            }

            setFrom(getDefaultDirectionPoint(fromResponse, defaultDirectionNames.from));
            setTo(getDefaultDirectionPoint(toResponse, defaultDirectionNames.to));
        };

        loadDefaultDirection().catch(() => undefined);

        return () => {
            isCancelled = true;
            defaultDirectionRequestVersion.current += 1;
        };
    }, []);

    const loadDirectionRates = useCallback(async () => {
        if (!from || !to || !directionTrailerFamilyType) {
            return;
        }

        const version = directionRequestVersion.current + 1;
        directionRequestVersion.current = version;
        setDirectionData(null);
        setDirectionState("loading");

        try {
            const response = await getAverageRatesByDestination({
                fromId: from.id,
                toId: to.id,
                periodType,
                trailerFamilyType: directionTrailerFamilyType,
                isVatTop,
            });
            if (directionRequestVersion.current !== version) {
                return;
            }

            if (!response) {
                setDirectionState("error");
                return;
            }

            setDirectionData({
                ...response,
                weeks: Array.isArray(response.weeks) ? response.weeks : [],
            });
            setDirectionState(response.lastSevenDaysRate ? "success" : "insufficient");
        } catch (_error) {
            if (directionRequestVersion.current === version) {
                setDirectionState("error");
            }
        }
    }, [directionTrailerFamilyType, from, isVatTop, periodType, to]);

    useEffect(() => {
        if (activeTab !== "directions") {
            return;
        }

        if (!from || !to || !directionTrailerFamilyType) {
            setDirectionData(null);
            setDirectionState("unselected");
            return;
        }

        loadDirectionRates();

        return () => {
            directionRequestVersion.current += 1;
        };
    }, [activeTab, directionTrailerFamilyType, from, loadDirectionRates, to]);

    const weeks = data?.weeks || [];
    const completedWeeks = weeks.filter(isCompletedWeek);
    const currentIndex = data?.lastSevenDaysIndex;
    const currentDifferences = currentIndex;
    const indexChartMaximum = Math.max(indexChartMinimum + 1, ...completedWeeks.map((week) => week.index));
    const directionWeeks = directionData?.weeks || [];
    const completedDirectionWeeks = directionWeeks.filter(isCompletedWeek);
    const currentDirectionRate = directionData?.lastSevenDaysRate;
    const currentDirectionDifferences = currentDirectionRate;
    const directionChartWeeks: DirectionChartWeek[] = completedDirectionWeeks.map((week) => ({
        ...week,
        percentileRange: Math.max(0, week.percentile75 - week.percentile25),
    }));
    const directionRange = currentDirectionRate
        ? paddedRange(currentDirectionRate.percentile25, currentDirectionRate.percentile75)
        : { min: 0, max: 0 };
    const directionIqrPosition = currentDirectionRate
        ? rangeSegmentPosition(
              currentDirectionRate.percentile25,
              currentDirectionRate.percentile75,
              directionRange.min,
              directionRange.max,
          )
        : { left: 0, width: 100 };
    const directionMedianPosition = currentDirectionRate
        ? rangePosition(currentDirectionRate.median, directionRange.min, directionRange.max)
        : 50;
    const chartDataKey = displayMode === "index" ? "index" : "median";
    const rateUnit = formatRateUnit(organization?.currency);
    const vatLabel = formatVatLabel(isVatTop);
    const trailerSelectProps = {
        inputProps: { name: "trailerFamilyType" },
        options: trailerOptions,
        value: trailerFamilyType,
        onChange: (nextValue: { value: TrailerFamilyFilter }) => setTrailerFamilyType(nextValue.value),
        unClearable: true,
        unSearchable: true,
    };
    const fromSelectProps = {
        inputProps: { name: "fromDestination" },
        url: getCityRegionsByName,
        propName: "name",
        filterPropName: "name",
        value: from?.id || null,
        placeholder: "Выберите",
        minimumInputLength: 2,
        onChange: ({ option }: { option: DirectionPoint | null }) => {
            defaultDirectionRequestVersion.current += 1;
            setFrom(option ? { id: option.id, name: option.name } : null);
            setTo(null);
        },
    };
    const toSelectProps = {
        inputProps: { name: "toDestination" },
        url: getCityRegionsByName,
        propName: "name",
        filterPropName: "name",
        value: to?.id || null,
        placeholder: "Выберите",
        minimumInputLength: 2,
        onChange: ({ option }: { option: DirectionPoint | null }) => {
            defaultDirectionRequestVersion.current += 1;
            setTo(option ? { id: option.id, name: option.name } : null);
        },
    };
    const directionTrailerSelectProps = {
        inputProps: { name: "directionTrailerFamilyType" },
        options: directionTrailerOptions,
        value: directionTrailerFamilyType,
        placeholder: "Выберите",
        onChange: ({ value }: { value: CargorunIndexTrailerFamilyType | null }) => setDirectionTrailerFamilyType(value),
        unClearable: true,
        unSearchable: true,
    };

    const close = () => {
        const { history, back } = props;
        back ? back() : history.push(paths.root);
    };

    return (
        <div className="page-content">
            <div className="page-col page-col--full mounted">
                <div className="page-book__list d-flex flex-column">
                    <div className="book-list__head cargorun-index__book-head">
                        <div className="book-list__head-close close close--sm" aria-label="Закрыть" onClick={close} />
                        <div className="book-list__head-title cargorun-index__head">
                            <div>Индекс CARGO.RUN FTL РФ</div>
                            <div className="cargorun-index__head-subtitle">
                                Данные актуальны для FTL-перевозок (20 т)
                            </div>
                        </div>
                    </div>

                    <div className="cargorun-index flex-grow-1 overflow-auto">
                        <div className="cargorun-index__inner">
                            <div className="cargorun-index__tabs" role="tablist" aria-label="Разделы индекса">
                                <button
                                    className={
                                        activeTab === "index"
                                            ? "cargorun-index__tab cargorun-index__tab--active"
                                            : "cargorun-index__tab"
                                    }
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === "index"}
                                    onClick={() => setActiveTab("index")}
                                >
                                    Индекс CARGO.RUN
                                </button>
                                <button
                                    className={
                                        activeTab === "directions"
                                            ? "cargorun-index__tab cargorun-index__tab--active"
                                            : "cargorun-index__tab"
                                    }
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === "directions"}
                                    onClick={() => setActiveTab("directions")}
                                >
                                    Динамика по направлениям
                                </button>
                            </div>

                            {activeTab === "index" && (
                                <>
                                    <section
                                        className="card card-body cargorun-index__filters"
                                        aria-label="Параметры отчета"
                                    >
                                        <div className="cargorun-index__filter-group">
                                            <label htmlFor="trailerFamilyTypeInput">Тип полуприцепа</label>
                                            <Select {...trailerSelectProps} />
                                        </div>

                                        <div className="cargorun-index__filter-group cargorun-index__filter-group--periods">
                                            <span>Период отображения</span>
                                            <div
                                                className="cargorun-index__periods"
                                                role="group"
                                                aria-label="Период отображения"
                                            >
                                                {periods.map((period) => (
                                                    <button
                                                        key={period.value}
                                                        className={
                                                            period.value === periodType
                                                                ? "cargorun-index__period cargorun-index__period--active"
                                                                : "cargorun-index__period"
                                                        }
                                                        type="button"
                                                        aria-pressed={period.value === periodType}
                                                        onClick={() => setPeriodType(period.value)}
                                                    >
                                                        {period.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {displayMode === "rate" && (
                                            <div className="cargorun-index__filter-group cargorun-index__filter-group--vat">
                                                <span>Режим учета НДС</span>
                                                <VatModeToggle isVatTop={isVatTop} onChange={setIsVatTop} />
                                            </div>
                                        )}
                                    </section>

                                    {state === "loading" && (
                                        <section className="card card-body cargorun-index__state" aria-live="polite">
                                            Загрузка индекса...
                                        </section>
                                    )}

                                    {state === "empty" && (
                                        <section className="card card-body cargorun-index__state" aria-live="polite">
                                            По выбранным параметрам данных пока нет.
                                        </section>
                                    )}

                                    {state === "error" && (
                                        <section className="card card-body cargorun-index__state" aria-live="assertive">
                                            <p>Не удалось получить данные индекса.</p>
                                            <button className="btn btn-primary" type="button" onClick={loadIndexes}>
                                                Повторить
                                            </button>
                                        </section>
                                    )}

                                    {state === "success" && currentIndex && data && currentDifferences && (
                                        <section className="card card-body cargorun-index__content">
                                            <aside className="cargorun-index__summary">
                                                <div className="cargorun-index__current">
                                                    <div className="cargorun-index__current-heading">
                                                        <span className="cargorun-index__current-label">
                                                            {displayMode === "index"
                                                                ? "Текущий индекс CARGO.RUN"
                                                                : "Текущая медианная ставка CARGO.RUN"}
                                                        </span>
                                                        <div
                                                            className="cargorun-index__display-mode"
                                                            role="group"
                                                            aria-label="Показатель индекса"
                                                        >
                                                            <button
                                                                className={
                                                                    displayMode === "index"
                                                                        ? "cargorun-index__display-mode-button cargorun-index__display-mode-button--active"
                                                                        : "cargorun-index__display-mode-button"
                                                                }
                                                                type="button"
                                                                aria-pressed={displayMode === "index"}
                                                                onClick={() => setDisplayMode("index")}
                                                            >
                                                                Пункты
                                                            </button>
                                                            <button
                                                                className={
                                                                    displayMode === "rate"
                                                                        ? "cargorun-index__display-mode-button cargorun-index__display-mode-button--active"
                                                                        : "cargorun-index__display-mode-button"
                                                                }
                                                                type="button"
                                                                aria-pressed={displayMode === "rate"}
                                                                onClick={() => setDisplayMode("rate")}
                                                            >
                                                                {rateUnit}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <strong className="d-flex cargorun-index__current--sub">
                                                        <ChartPointArrow
                                                            className={currentIndexArrowClass(
                                                                currentDifferences.weekDifferences,
                                                            )}
                                                        />
                                                        {displayMode === "index" ? (
                                                            <>
                                                                {formatNumber(currentIndex.index, {
                                                                    maximumFractionDigits: 1,
                                                                })}
                                                                <span className="pl-1 font-size-18">п.</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {formatRate(currentIndex.median)}
                                                                <span className="pl-1 font-size-18">
                                                                    {rateUnit}, {vatLabel}
                                                                </span>
                                                            </>
                                                        )}
                                                    </strong>
                                                    <span className="cargorun-index__current-date text-muted">
                                                        за последние 7 дней
                                                    </span>
                                                </div>

                                                <div className="cargorun-index__differences border p-3">
                                                    {differences.map((difference) => {
                                                        const value = currentDifferences[difference.key];
                                                        return (
                                                            <div
                                                                className="cargorun-index__difference"
                                                                key={difference.key}
                                                            >
                                                                <div className={differenceArrowContainerClass(value)}>
                                                                    <ChartPointArrow
                                                                        className={differenceArrowClass(value)}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <span>{difference.label}</span>
                                                                    <small className="text-muted">
                                                                        {difference.period}
                                                                    </small>
                                                                </div>
                                                                <strong className={differenceState(value)}>
                                                                    {formatDifference(value)}
                                                                </strong>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </aside>

                                            <div className="cargorun-index__chart">
                                                <div className="cargorun-index__chart-area">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <AreaChart
                                                            data={completedWeeks}
                                                            margin={{ top: 18, right: 18, left: 0, bottom: 8 }}
                                                        >
                                                            <CartesianGrid strokeDasharray="3 4" />
                                                            <XAxis
                                                                dataKey="week"
                                                                tickFormatter={formatChartWeek}
                                                                minTickGap={32}
                                                            />
                                                            <YAxis
                                                                width={48}
                                                                allowDecimals={false}
                                                                allowDataOverflow={displayMode === "index"}
                                                                domain={
                                                                    displayMode === "index"
                                                                        ? [indexChartMinimum, indexChartMaximum]
                                                                        : undefined
                                                                }
                                                            />
                                                            <Tooltip
                                                                content={
                                                                    <IndexTooltip
                                                                        currency={organization?.currency}
                                                                        vatLabel={vatLabel}
                                                                    />
                                                                }
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey={chartDataKey}
                                                                stroke="#ffb300"
                                                                strokeWidth={3}
                                                                fill="#ffb300"
                                                                fillOpacity={0.2}
                                                                dot={{
                                                                    r: 3,
                                                                    fill: "#ffb300",
                                                                    stroke: "#ffb300",
                                                                    strokeWidth: 5,
                                                                }}
                                                                activeDot={{
                                                                    r: 8,
                                                                    fill: "#ffb300",
                                                                    stroke: "#ffffff",
                                                                    strokeWidth: 3,
                                                                }}
                                                            />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>

                                            <div className="cargorun-index__hints">
                                                {displayMode === "index" && (
                                                    <p className="cargorun-index__index-note">
                                                        Значение пунктов означает, что текущая ставка примерно в{" "}
                                                        {formatNumber(currentIndex.index / indexBaseValue, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}{" "}
                                                        раза выше базового уровня января 2021 года.
                                                    </p>
                                                )}
                                                <p className="cargorun-index__methodology">
                                                    Информация по методологии расчётов индекса:{" "}
                                                    <a href={indexMethodologyUrl} target="_blank" rel="noreferrer">
                                                        {indexMethodologyUrl}
                                                    </a>
                                                </p>
                                            </div>
                                        </section>
                                    )}
                                </>
                            )}

                            {activeTab === "directions" && (
                                <>
                                    <section
                                        className="card card-body cargorun-index__filters cargorun-index__filters--directions"
                                        aria-label="Параметры отчета по направлению"
                                    >
                                        <div className="cargorun-index__route-fields">
                                            <div className="cargorun-index__filter-group">
                                                <label htmlFor="fromDestinationInput">Город отправления</label>
                                                <Select {...fromSelectProps} />
                                            </div>
                                            <span className="cargorun-index__route-arrow" aria-hidden="true">
                                                →
                                            </span>
                                            <div className="cargorun-index__filter-group">
                                                <label htmlFor="toDestinationInput">Город назначения</label>
                                                <Select {...toSelectProps} />
                                            </div>
                                        </div>

                                        <div className="cargorun-index__filter-group">
                                            <label htmlFor="directionTrailerFamilyTypeInput">Тип прицепа</label>
                                            <Select {...directionTrailerSelectProps} />
                                        </div>

                                        <div className="cargorun-index__filter-group cargorun-index__filter-group--periods">
                                            <span>Период отображения</span>
                                            <div
                                                className="cargorun-index__periods"
                                                role="group"
                                                aria-label="Период отображения"
                                            >
                                                {periods.map((period) => (
                                                    <button
                                                        key={period.value}
                                                        className={
                                                            period.value === periodType
                                                                ? "cargorun-index__period cargorun-index__period--active"
                                                                : "cargorun-index__period"
                                                        }
                                                        type="button"
                                                        aria-pressed={period.value === periodType}
                                                        onClick={() => setPeriodType(period.value)}
                                                    >
                                                        {period.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="cargorun-index__filter-group cargorun-index__filter-group--vat">
                                            <span>Режим учета НДС</span>
                                            <VatModeToggle isVatTop={isVatTop} onChange={setIsVatTop} />
                                        </div>
                                    </section>

                                    {directionState === "unselected" && (
                                        <section className="card card-body cargorun-index__state cargorun-index__direction-state">
                                            <span className="cargorun-index__direction-state-icon" aria-hidden="true">
                                                <IconMapPin size={34} />
                                            </span>
                                            <h2>Выберите направление</h2>
                                            <p>
                                                Укажите город отправления, город назначения и тип прицепа, чтобы
                                                посмотреть динамику ставок FTL-перевозок.
                                            </p>
                                        </section>
                                    )}

                                    {directionState === "loading" && (
                                        <section className="card card-body cargorun-index__state" aria-live="polite">
                                            Загрузка данных по направлению...
                                        </section>
                                    )}

                                    {directionState === "insufficient" && (
                                        <section className="card card-body cargorun-index__state cargorun-index__direction-state">
                                            <span className="cargorun-index__direction-state-icon cargorun-index__direction-state-icon--warning">
                                                <IconExclamationCircle size={34} />
                                            </span>
                                            <h2>Недостаточно данных по направлению</h2>
                                            <p>
                                                По направлению{" "}
                                                <b>
                                                    {from?.name} → {to?.name}
                                                </b>{" "}
                                                и выбранному типу прицепа недостаточно заявок для расчета статистики.
                                                Попробуйте выбрать другой период или тип прицепа.
                                            </p>
                                        </section>
                                    )}

                                    {directionState === "error" && (
                                        <section className="card card-body cargorun-index__state" aria-live="assertive">
                                            <p>Не удалось получить данные по направлению.</p>
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={loadDirectionRates}
                                            >
                                                Повторить
                                            </button>
                                        </section>
                                    )}

                                    {directionState === "success" &&
                                        currentDirectionRate &&
                                        currentDirectionDifferences && (
                                            <section className="card card-body cargorun-index__content cargorun-index__content--directions">
                                                <aside className="cargorun-index__summary cargorun-index__direction-summary">
                                                    <div className="cargorun-index__current cargorun-index__direction-current">
                                                        <strong className="cargorun-index__direction-route">
                                                            {from?.name} → {to?.name}
                                                        </strong>
                                                        <span className="text-muted">Медианная ставка</span>
                                                        <strong className="cargorun-index__direction-rate">
                                                            {formatRate(currentDirectionRate.median)}
                                                            <span className="text-muted">
                                                                {rateUnit}, {vatLabel}
                                                            </span>
                                                        </strong>
                                                        <div className="cargorun-index__direction-current-meta text-muted">
                                                            <span>за последние 7 дней</span>
                                                            <span>
                                                                {formatNumber(currentDirectionRate.count)}{" "}
                                                                {ending(
                                                                    currentDirectionRate.count,
                                                                    "заявка",
                                                                    "заявки",
                                                                    "заявок",
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="cargorun-index__direction-range">
                                                        <h3>Диапазон ставок — последние 7 дней, {vatLabel}</h3>
                                                        <div className="cargorun-index__direction-range-line">
                                                            <span
                                                                className="cargorun-index__direction-range-iqr"
                                                                style={{
                                                                    left: `${directionIqrPosition.left}%`,
                                                                    width: `${directionIqrPosition.width}%`,
                                                                }}
                                                            />
                                                            <span
                                                                className="cargorun-index__direction-range-median"
                                                                style={{
                                                                    left: `${directionMedianPosition}%`,
                                                                }}
                                                            />
                                                            <span
                                                                className="cargorun-index__direction-range-median-value"
                                                                style={{ left: `${directionMedianPosition}%` }}
                                                                aria-label={`Медианная ставка: ${formatRate(
                                                                    currentDirectionRate.median,
                                                                )}`}
                                                            >
                                                                {formatRate(currentDirectionRate.median)}
                                                            </span>
                                                        </div>
                                                        <div className="cargorun-index__direction-range-values text-muted">
                                                            {currentDirectionRate.percentile25 ===
                                                            currentDirectionRate.percentile75 ? (
                                                                <span
                                                                    className="cargorun-index__direction-range-value"
                                                                    style={{ left: `${directionMedianPosition}%` }}
                                                                >
                                                                    <span>
                                                                        {formatRate(currentDirectionRate.percentile25)}
                                                                    </span>
                                                                    <span className="cargorun-index__direction-range-percentile">
                                                                        Р25/Р75
                                                                    </span>
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <span
                                                                        className="cargorun-index__direction-range-value"
                                                                        style={{
                                                                            left: `${directionIqrPosition.left}%`,
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {formatRate(
                                                                                currentDirectionRate.percentile25,
                                                                            )}
                                                                        </span>
                                                                        <span className="cargorun-index__direction-range-percentile">
                                                                            Р25
                                                                        </span>
                                                                    </span>
                                                                    <span
                                                                        className="cargorun-index__direction-range-value"
                                                                        style={{
                                                                            left: `${directionIqrPosition.left + directionIqrPosition.width}%`,
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {formatRate(
                                                                                currentDirectionRate.percentile75,
                                                                            )}
                                                                        </span>
                                                                        <span className="cargorun-index__direction-range-percentile">
                                                                            Р75
                                                                        </span>
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="cargorun-index__direction-range-footer">
                                                            <span className="text-muted">Р25–Р75 — середина рынка</span>
                                                            <strong>
                                                                Среднее:{" "}
                                                                {formatRatePerKilometer(
                                                                    currentDirectionRate.mean,
                                                                    organization?.currency,
                                                                )}{" "}
                                                                {vatLabel}
                                                            </strong>
                                                        </div>
                                                    </div>

                                                    <div className="cargorun-index__differences cargorun-index__direction-differences border p-3">
                                                        {differences.map((difference) => {
                                                            const value = currentDirectionDifferences[difference.key];
                                                            return (
                                                                <div
                                                                    className="cargorun-index__difference"
                                                                    key={difference.key}
                                                                >
                                                                    <div
                                                                        className={differenceArrowContainerClass(value)}
                                                                    >
                                                                        <ChartPointArrow
                                                                            className={differenceArrowClass(value)}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <span>{difference.label}</span>
                                                                        <small className="text-muted">
                                                                            {difference.period}
                                                                        </small>
                                                                    </div>
                                                                    <div className="cargorun-index__direction-difference-values">
                                                                        <strong>
                                                                            {formatRateDifference(
                                                                                currentDirectionRate.median,
                                                                                value,
                                                                                organization?.currency,
                                                                            )}{" "}
                                                                            {vatLabel}
                                                                        </strong>
                                                                        <strong className={differenceState(value)}>
                                                                            {formatDifference(value)}
                                                                        </strong>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </aside>

                                                <div className="cargorun-index__chart cargorun-index__direction-chart">
                                                    <div className="cargorun-index__direction-chart-head">
                                                        <div>
                                                            <h2>Динамика ставки по направлению</h2>
                                                            <p>
                                                                {from?.name} → {to?.name} ·{" "}
                                                                {directionTrailerOptions
                                                                    .find(
                                                                        (option) =>
                                                                            option.value === directionTrailerFamilyType,
                                                                    )
                                                                    ?.label.toLowerCase()}{" "}
                                                                · по неделям · {vatLabel}
                                                            </p>
                                                        </div>
                                                        <div
                                                            className="cargorun-index__direction-legend"
                                                            aria-label="Обозначения графика"
                                                        >
                                                            <span className="cargorun-index__direction-legend-median">
                                                                Медиана
                                                            </span>
                                                            <span className="cargorun-index__direction-legend-iqr">
                                                                Р25–Р75
                                                            </span>
                                                            <span className="cargorun-index__direction-legend-mean">
                                                                Среднее
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="cargorun-index__chart-area">
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <AreaChart
                                                                data={directionChartWeeks}
                                                                margin={{ top: 18, right: 18, left: 12, bottom: 8 }}
                                                            >
                                                                <CartesianGrid strokeDasharray="3 4" />
                                                                <XAxis
                                                                    dataKey="week"
                                                                    tickFormatter={formatChartWeek}
                                                                    minTickGap={32}
                                                                />
                                                                <YAxis
                                                                    width={66}
                                                                    tickFormatter={(value) =>
                                                                        `${formatNumber(value, { maximumFractionDigits: 0 })} ${rateUnit.replace("/км", "")}`
                                                                    }
                                                                />
                                                                <Tooltip
                                                                    content={
                                                                        <DirectionTooltip
                                                                            currency={organization?.currency}
                                                                            vatLabel={vatLabel}
                                                                        />
                                                                    }
                                                                />
                                                                <Area
                                                                    type="monotone"
                                                                    dataKey="percentile25"
                                                                    stackId="quartile"
                                                                    stroke="none"
                                                                    fill="transparent"
                                                                    isAnimationActive={false}
                                                                />
                                                                <Area
                                                                    type="monotone"
                                                                    dataKey="percentileRange"
                                                                    stackId="quartile"
                                                                    stroke="none"
                                                                    fill="#ffb300"
                                                                    fillOpacity={0.36}
                                                                    isAnimationActive={false}
                                                                />
                                                                <Area
                                                                    type="monotone"
                                                                    dataKey="median"
                                                                    stroke="#ffb300"
                                                                    strokeWidth={3}
                                                                    fill="transparent"
                                                                    dot={false}
                                                                    activeDot={{
                                                                        r: 7,
                                                                        fill: "#ffb300",
                                                                        stroke: "#ffffff",
                                                                        strokeWidth: 3,
                                                                    }}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="mean"
                                                                    stroke="#ffb300"
                                                                    strokeDasharray="5 5"
                                                                    strokeWidth={2}
                                                                    dot={false}
                                                                    activeDot={false}
                                                                />
                                                            </AreaChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                            </section>
                                        )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state: { profile: { organization?: OrganizationProfileModel } }) => ({
    organization: state.profile.organization,
});

export default connect(mapStateToProps)(CargorunIndex);
